import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Checkbox, FontIcon } from 'react-toolbox';
import ReactTooltip from 'react-tooltip';
import LineItemStatus from 'lineItemStatus';
import { getPricePatchItems } from '../../../../../../../selectors/phaseLineItem';
import DropDownCell from 'components/LineItemTable/dropDownCell';
import InputCell from 'components/LineItemTable/inputCell';
import ItemNote from 'components/LineItemTable/itemNote';
import ItemEvents from 'components/LineItemTable/itemEvents';
import { EditableTableRow, EditableTableCell } from 'components/EditableTable';
import { pick, has } from 'lodash';
import phaseLineItemShape from 'types/lineItemPropType';
import { purchaseOrderOptionsType } from 'types/purchaseOrder';
import { calculateTotal, formatDollars, filterByVendor } from '../../../../../../../utils/lineItemUtils';
import { CONTRACT_VERIFICATION_STATUS_COLORS, LABOR_VERIFICATION_STATUSES,
         PRODUCT_VERIFICATION_STATUSES, ASSESSMENT_LABOR_ITEMS,
         ADDTL_MILEAGE_ITEMS, ASSESSMENT_DETAIL_ITEMS } from 'config/constants';
import RTCheckbox from 'styles/checkbox.scss';
import S from 'styles/table.scss';
import formStyles from 'styles/form';
import Editable from 'mixins/editable';

const EDITABLE_KEYS = ['status', 'line_item_id', 'payable', 'price', 'kind', 'quantity', 'total',
  'purchase_order_id', 'note'];

class LaborTableRow extends Editable(React.Component) {
  state = { ...this.props.lineItem, purchase_order_id: this.props.purchaseOrderId };

  getPurchaseOrderLabel = id => (this.props.purchaseOrderOptions.find(po => po.value === id) || {});

  handleSave = (keys = EDITABLE_KEYS) =>
    this.props.onSave(pick(this.state, keys.concat(['id'])));

  handleDelete = () => this.props.onDelete(this.state.id);

  handleChange = attr => (val) => {
    const lineItem = this.state;
    let value = this.sanatize(attr, val);
    if (attr === 'payable') value = !value;
    const price = attr === 'price' ? value : lineItem.price;
    const qty = attr === 'quantity' ? value : lineItem.quantity;
    const calculatedTotal = calculateTotal(price, qty);
    this.setState({ [attr]: value, calculatedTotal });
  }

  handleStatusChange = ({ status }) => this.setState({ status }, () => {
    if (!this.context.isEditable) this.handleSave(['status']);
  });

  handleDescriptionChange = (val) => {
    const lineItem = this.state;
    const { lineItemOptions } = this.props;
    const option = lineItemOptions.find(o => o.value === val);
    const { price, payable, kind } = option;
    const calculatedTotal = calculateTotal(price, lineItem.quantity);
    this.setState({ line_item_id: val, payable, calculatedTotal, price, kind });
  }

  handlePOChange = this.handleChange('purchase_order_id');
  handlePriceChange = this.handleChange('price');
  handleQuantityChange = this.handleChange('quantity');
  handleTotalChange = this.handleChange('total');
  handlePayableChange = this.handleChange('payable');
  handleNoteChange = this.handleChange('note');
  handlePayableSave = val => this.setState({ payable: !val }, () => { this.handleSave(['payable']); });

  blockItemCellEdit = (options, item) => (this.props.phaseType === 'Assessment' &&
    !options.some(o => (o.label.includes(item.item_number) || o.label.includes(item.description))));

  renderStatusCell = () => {
    const lineItem = this.state;
    const { readOnly } = this.props;
    const statuses = lineItem.kind === 'Labor' ? LABOR_VERIFICATION_STATUSES : PRODUCT_VERIFICATION_STATUSES;
    const val = readOnly ? lineItem.status : (<LineItemStatus
      onChange={this.handleStatusChange} lineItem={lineItem} options={statuses}
    />);
    return (<EditableTableCell
      onChange={this.handleStatusChange} label="Status" value={val} className={S.largeColumn}
    >{val}</EditableTableCell>);
  }

  renderPayable = () => {
    const checked = has(this.state, 'payable') ? !this.state.payable : false;
    const value = (<Checkbox
      checked={checked}
      onChange={this.handlePayableSave}
      className={formStyles.checkboxListItem}
      theme={RTCheckbox}
    />);
    return (<EditableTableCell
      label="Not Payable" value={value} onChange={this.handlePayableChange}
    >{value}</EditableTableCell>);
  }

  render = () => {
    const lineItem = this.state;
    const { purchaseOrderOptions, lineItemOptions, role, filterLineItems,
      surfaceType, phaseSource,
    } = this.props;
    const { isEditable } = this.context;
    const style = {
      background: CONTRACT_VERIFICATION_STATUS_COLORS[lineItem.status],
    };
    const pricePatchCellReadOnly = role !== 'admin';
    const rowProps = {
      isNew: lineItem.id < 0, style, onSave: this.handleSave, onDelete: this.handleDelete,
    };
    const calculatedTotalValue = (<span className={S.iconContainer}>
      {formatDollars(lineItem.calculatedTotal)}
      {lineItem.total !== lineItem.calculatedTotal && <FontIcon
        data-tip data-for={`${lineItem.id}lineItemCalculatedTotalError`}
        value="ic_warning"
        className={S.calculatedTotalIcon}
      />}
      {lineItem.total !== lineItem.calculatedTotal && <ReactTooltip
        id={`${lineItem.id}lineItemCalculatedTotalError`} place="top" effect="solid"
      >Calculated total and PO line item total do not match.</ReactTooltip>}
    </span>);
    const optionsByVendor = filterByVendor(lineItemOptions, phaseSource);
    return (<EditableTableRow key={lineItem.id} id={lineItem.id} {...rowProps}>
      <ItemEvents
        display={!isEditable}
        label=""
        itemType="phaseLineItems"
        lineItem={lineItem}
        dataKeys={['status', 'po_number', 'item_number', 'description',
          'quantity', 'price', 'total', 'payable', 'note']}
      />
      {this.renderStatusCell()}
      <DropDownCell
        attr="purchase_order"
        display={isEditable}
        isIdAttr
        label="PO Number"
        lineItem={lineItem}
        options={purchaseOrderOptions}
        cellValue={this.getPurchaseOrderLabel(lineItem.purchase_order_id).label}
        onChange={this.handlePOChange}
      />
      <EditableTableCell display={!isEditable} value={lineItem.kind} label="Kind" />
      <EditableTableCell display={!isEditable} value={lineItem.item_number} label="Item Number" />
      <EditableTableCell display={!isEditable} value={lineItem.description} label="Description" />
      {this.blockItemCellEdit(lineItemOptions, lineItem) && lineItem.id >= 0 ? (<EditableTableCell
        value={lineItem.item_number}
        display={isEditable}
        label="Item Number - Description"
      >{lineItem.item_number} - {lineItem.description}
      </EditableTableCell>) : (<DropDownCell
        attr="line_item"
        isIdAttr
        label="Item Number - Description"
        display={isEditable}
        lineItem={lineItem}
        options={(!filterLineItems ? optionsByVendor : optionsByVendor
          .filter(o =>
            !o.surface_type ||
            o.label.includes(lineItem.item_number || null) ||
            [surfaceType, 'Multi', 'All'].includes(o.surface_type)))
        }
        onChange={this.handleDescriptionChange}
      />)}
      <InputCell
        attr="quantity"
        label="QTY"
        lineItem={lineItem}
        onChange={this.handleQuantityChange}
      />
      <InputCell
        attr="price"
        label="Price Patch"
        lineItem={lineItem}
        onChange={this.handlePriceChange}
        cellValue={formatDollars(lineItem.price)}
        cellReadOnly={pricePatchCellReadOnly}
      />
      <EditableTableCell className={S.calculatedTotalColumn} value={calculatedTotalValue} label="Calculated Total">
        {calculatedTotalValue}
      </EditableTableCell>
      <InputCell
        attr="total"
        type="number"
        label="PO Line Item Total"
        lineItem={lineItem}
        onChange={this.handleTotalChange}
        cellValue={formatDollars(lineItem.total)}
      />
      {this.renderPayable()}
      <ItemNote display label="Note" onChange={this.handleNoteChange} note={lineItem.note} id={lineItem.id} kind="lineItem" />
    </EditableTableRow>);
  }
}

LaborTableRow.contextTypes = { isEditable: PropTypes.bool };

LaborTableRow.propTypes = {
  lineItem: PropTypes.shape(phaseLineItemShape),
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  lineItemOptions: PropTypes.array,
  purchaseOrderOptions: purchaseOrderOptionsType,
  purchaseOrderId: PropTypes.number,
  readOnly: PropTypes.bool,
  role: PropTypes.string,
  phaseType: PropTypes.string,
  filterLineItems: PropTypes.bool,
  phaseSource: PropTypes.string,
};

LaborTableRow.defaultProps = {
  lineItem: {},
  lineItemOptions: [],
  itemType: 'Product',
  purchaseOrderId: null,
  filterLineItems: false,
};

const getItemNumberFromLabel = ({ label }) => label.substring(0, label.indexOf(' - '));

const mapStateToProps = (state) => {
  const { phase: { details: { type, source, surface_type: { name } } } } = state;
  return ({
    lineItemOptions: type !== 'Assessment' ?
      getPricePatchItems(state) : getPricePatchItems(state).filter(i =>
        ASSESSMENT_LABOR_ITEMS.includes(getItemNumberFromLabel(i)) ||
        ADDTL_MILEAGE_ITEMS.includes(getItemNumberFromLabel(i)) ||
        ASSESSMENT_DETAIL_ITEMS.includes(getItemNumberFromLabel(i)),
      ),
    phaseType: type,
    phaseSource: source,
    surfaceType: name,
  });
};

export default connect(mapStateToProps)(LaborTableRow);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/components/laborTableRow.jsx